import { AdHocPasteInButton } from "@/components/cart/AdHocPasteIn/AdHocPasteInButton";
import { CartFilterSidebar } from "@/components/cart/CartFilterSidebar";
import { CartPageColumn, CartPageRoot } from "@/components/cart/CartPageColumn";
import {
  CartPageError,
  CartPageSkeleton
} from "@/components/cart/CartPageStates";
import { CartToolbar } from "@/components/cart/CartToolbar";
import {
  CartViewToggle,
  type CartView
} from "@/components/cart/CartViewToggle";
import { CompareAdHocNote } from "@/components/cart/CompareAdHocNote";
import { ContinueShoppingLink } from "@/components/cart/ContinueShoppingLink";
import { EmptyCartState as NewEmptyCartState } from "@/components/cart/EmptyCartState";
import { MatrixCheckoutBar } from "@/components/cart/MatrixCheckoutBar";
import { MobileCartSummaryBar } from "@/components/cart/MobileCartSummaryBar";
import { QuoteDiffBanner } from "@/components/cart/QuoteDiffBanner";
import { SaveCartAsListButton } from "@/components/cart/SaveCartAsListButton";
import { SellerCardList } from "@/components/cart/SellerCardList";
import { SupplierComparisonGrid } from "@/components/cart/SupplierComparisonGrid";
import { CheckoutProgress } from "@/components/checkout/CheckoutProgress";
import { OrderSummary } from "@/components/order-summary";
import { SUPPORT_FAB_BAR_CLEARANCE } from "@/components/support/WhatsAppSupportButton";
import { useBreakpoint } from "@/hooks/useBreakpoint";
import { useCartPromoCodes } from "@/hooks/useCartPromoCodes";
import { useCartSelection } from "@/hooks/useCartSelection";
import { useIsConciergeOperator } from "@/hooks/useIsConciergeOperator";
import { useReportOnce } from "@/hooks/useReportOnce";
import { analytics } from "@/lib/analytics";
import {
  MIXED_PAYMENT_MODES_MESSAGE,
  hasExternalCheckoutSeller,
  resolveCombinedCheckout
} from "@/lib/supplier-collected";
import { useSellerCarts } from "@/queries/cart";
import { useProductCategories } from "@/queries/categories";
import {
  calculateSummary,
  collectCartCategories,
  countAdHocLines,
  filterCartsByCategories
} from "@/utils/cart";
import { cn } from "@repo/design-system/utils";
import { toast } from "@medusajs/ui";
import { createRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";
import { layoutRoute } from "../$layoutRoute";
import {
  cartSearchSchema,
  DEFAULT_CART_TAB,
  type CartTabValue
} from "./search";

export type { CartTabValue } from "./search";

export const cartRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "cart",
  component: CartPage,
  validateSearch: cartSearchSchema
});

const DEFAULT_CART_IDS: string[] = [];

/**
 * The cart page (KAN-68): a slim toolbar over a two-column layout.
 *
 * Layout:
 * - Toolbar: a Filter popover and the Cart / Compare toggle.
 *   Only shown when the cart spans multiple suppliers or categories.
 * - Cart view: seller cards (left) + the order-summary roll-up (right).
 * - Compare view: the full-width Items x Suppliers matrix.
 *
 * The active view lives in the URL via `?tab` so Compare is deep-linkable
 * (KAN-38): `/cart?tab=review-matrix` lands a concierge link straight on it.
 */
function CartPage() {
  // Tab state lives in the URL so the matrix view is deep-linkable (KAN-38).
  const { tab: tabValue = DEFAULT_CART_TAB } = useSearch({
    from: cartRoute.id
  });
  const navigate = useNavigate();

  // The ad hoc paste-in entry point (KAN-193) is the first surface in the app
  // that branches on actor type - kept self-contained to this one read here,
  // rather than threading an `isConciergeOperator` prop through the tree.
  const isConciergeOperator = useIsConciergeOperator();

  const setTabValue = useCallback(
    (next: CartTabValue) => {
      // `replace` so flicking between views does not stack history entries the
      // back button then has to walk through.
      navigate({ to: "/cart", search: { tab: next }, replace: true });
    },
    [navigate]
  );

  const isMatrixView = tabValue === "review-matrix";

  // The Cart / Compare toggle maps onto `?tab`: Compare is the matrix,
  // everything else is the seller-card view.
  const setView = useCallback(
    (next: CartView) => {
      setTabValue(next === "compare" ? "review-matrix" : "by-supplier");
    },
    [setTabValue]
  );

  // Responsive layout branches through the single breakpoint source (KAN-73):
  // `>= lg` is the desktop two-column, below `md` the summary becomes a sticky
  // bottom bar, and the tablet band between them stacks with the summary as an
  // inline card. This is a behaviour branch (which component to mount), so it
  // reads the breakpoint in JS rather than only via `lg:` classes.
  const isDesktop = useBreakpoint("lg");
  const isPhone = !useBreakpoint("md");

  // Fetch all carts (unfiltered)
  const { data: allCartsData, isLoading, error } = useSellerCarts();
  const allCartIds: string[] = useMemo(() => {
    return allCartsData?.carts.map((c) => c.cart_id) ?? DEFAULT_CART_IDS;
  }, [allCartsData?.carts]);

  // Cart selection state
  const {
    selectedCartIds,
    hasSelection,
    toggleCart,
    selectAll,
    isAllSelected
  } = useCartSelection({
    allCartIds,
    defaultSelectAll: true
  });

  // Categories aren't on cart line items, so they're fetched for the cart's
  // products and joined client-side to drive the category filter.
  const cartProductIds = useMemo(
    () =>
      Array.from(
        new Set(
          (allCartsData?.carts ?? []).flatMap((cart) =>
            cart.items
              .map((item) => item.product_id)
              .filter((id): id is string => Boolean(id))
          )
        )
      ),
    [allCartsData?.carts]
  );
  const { data: categoriesByProduct } = useProductCategories(cartProductIds);

  const categoryOptions = useMemo(
    () =>
      collectCartCategories(
        allCartsData?.carts ?? [],
        categoriesByProduct ?? {}
      ),
    [allCartsData?.carts, categoriesByProduct]
  );
  const categoryIds = useMemo(
    () => categoryOptions.map((category) => category.id),
    [categoryOptions]
  );

  // Reuses the same multi-select hook the supplier filter uses (it's generic
  // despite the "cart" naming) so categories start fully ticked - like every
  // supplier does - and newly-discovered categories (a product added later)
  // are auto-included rather than silently excluded until the user notices.
  const {
    selectedCartIds: selectedCategoryIds,
    toggleCart: toggleCategory,
    // "Clear" for categories means "stop restricting", i.e. tick them all -
    // not the hook's `clearAll`, which unticks everything. Unticking would
    // leave the sidebar showing zero ticked boxes while every product stayed
    // visible (an empty selection is treated as "no filter" downstream), and
    // the Clear button would remain on screen as if a filter were still
    // active. Categories have no other control to restore them, so they are
    // re-ticked here rather than cleared.
    selectAll: clearCategories,
    isAllSelected: isAllCategoriesSelected
  } = useCartSelection({
    allCartIds: categoryIds,
    defaultSelectAll: true
  });

  // Supplier filter selects whole carts; the category filter then narrows the
  // line items within them (and restates those carts' totals).
  const filteredCarts = useMemo(() => {
    const bySupplier =
      allCartsData?.carts.filter((cart) =>
        selectedCartIds.includes(cart.cart_id)
      ) ?? [];
    // Alphabetical by supplier, so the seller cards (left) list in the same
    // order as the order summary (right) instead of cart-creation order.
    return filterCartsByCategories(
      [...bySupplier].sort((a, b) =>
        a.seller.name.localeCompare(b.seller.name)
      ),
      selectedCategoryIds,
      categoriesByProduct ?? {}
    );
  }, [
    allCartsData?.carts,
    selectedCartIds,
    selectedCategoryIds,
    categoriesByProduct
  ]);

  // The review-matrix tab opened. Gives `supplier_switched` a denominator:
  // "opened the grid, compared, did not switch" is the most common outcome and
  // is otherwise invisible, so decision 4's thesis could not be evidenced
  // either way.
  //
  // The *tab* is the occurrence: the key goes null on leaving it, which re-arms
  // for the next opening. Having carts is readiness, not a second occurrence, so
  // it belongs in `ready` - filtering the grid down to nothing and then clearing
  // the filter holds the latch instead of re-arming it. (Keyed on
  // `isMatrixView && filteredCarts.length > 0`, which is what this was, it
  // reported twice for one opening.)
  //
  // `supplier_count` is the number of supplier *columns* the matrix shows, which
  // spans every supplier carrying the cart's products and so is only known once
  // the grid has loaded its offers - hence it is reported up by the grid, and
  // the event waits for it (KAN-254). Counting `filteredCarts` instead gave the
  // number of suppliers already in the cart: 2 for a 13-column matrix.
  const [matrixColumnCount, setMatrixColumnCount] = useState<number | null>(
    null
  );
  useReportOnce(
    isMatrixView ? "review-matrix" : null,
    () => {
      analytics.comparisonMatrixViewed({
        line_count: new Set(
          filteredCarts.flatMap((cart) =>
            (cart.items ?? []).map((item) => item.product_id)
          )
        ).size,
        supplier_count: matrixColumnCount ?? 0
      });
    },
    filteredCarts.length > 0 && matrixColumnCount !== null
  );

  // Seller cards start collapsed on every visit: the header carries each
  // supplier's name, item count, total and checkout action, and the buyer
  // expands a card to see its lines. We track the *expanded* set (empty = all
  // closed), so a supplier added later also starts closed rather than opening
  // unannounced.
  const [expandedCartIds, setExpandedCartIds] = useState<Set<string>>(
    () => new Set()
  );
  const toggleCard = useCallback((cartId: string) => {
    setExpandedCartIds((prev) => {
      const next = new Set(prev);
      if (!next.delete(cartId)) {
        next.add(cartId);
      }
      return next;
    });
  }, []);

  // Promo code management
  const { appliedPromoCodes, promoErrors, applyPromo, removePromo } =
    useCartPromoCodes();

  // Calculate filtered summary
  const filteredSummary = useMemo(
    () => calculateSummary(filteredCarts),
    [filteredCarts]
  );

  // Ad hoc supplier items (KAN-193) can't be a row in the cross-supplier
  // price matrix (no product_id, no other-supplier offers), so the Compare
  // view acknowledges them with a note rather than letting them look lost.
  // Counted over the same filtered scope the grid draws from.
  const adHocLineCount = useMemo(
    () => countAdHocLines(filteredCarts),
    [filteredCarts]
  );

  // An external-checkout (e.g. Shopify) seller redirects to its own store, so a
  // combined checkout can never cover what the salon sees. "Checkout all" is
  // hidden whenever one is among the buyer's carts. Judged over ALL carts, not
  // the filtered view, so unticking that supplier or narrowing by category
  // cannot bring the button back; per-supplier buttons stay.
  const canCheckoutAll = useMemo(
    () => !hasExternalCheckoutSeller(allCartsData?.carts ?? []),
    [allCartsData?.carts]
  );

  // Checkout handler - navigates to the internal checkout with the selected
  // carts. Only reachable when no external-checkout seller is among any of the
  // buyer's carts (`canCheckoutAll`, checked over all carts), so what remains
  // to resolve is a selection spanning both payment modes, which is *blocked*
  // with a message - a supplier-collected cart and a marketplace cart cannot
  // share one payment step (KAN-131). Only the combined action is blocked;
  // per-supplier buttons stay usable, which is what the message tells the
  // salon to reach for. `resolveCombinedCheckout` still drops external
  // sellers, so none could reach Stripe from here.
  const handleCheckoutAll = useCallback(() => {
    const plan = resolveCombinedCheckout(filteredCarts);

    if (!plan.ok) {
      toast.error(MIXED_PAYMENT_MODES_MESSAGE.title, {
        description: MIXED_PAYMENT_MODES_MESSAGE.description
      });
      return;
    }

    if (plan.cartIds.length === 0) return;

    navigate({
      to: "/checkout",
      search: {
        cart_ids: plan.cartIds.join(","),
        step: "information"
      }
    });
  }, [filteredCarts, navigate]);

  // What every summary surface receives: undefined hides its button.
  const onCheckoutAll = canCheckoutAll ? handleCheckoutAll : undefined;

  // Error state. Checked before the empty cart, because a failed request leaves
  // `allCartsData` undefined and would otherwise read as "your cart is empty".
  // Only when there is nothing to show: a failed background refetch keeps the
  // last good data alongside the error, and must not blank a loaded cart.
  if (error && !allCartsData) {
    return <CartPageError error={error} />;
  }

  // Empty cart state (truly empty - no items at all)
  if (!isLoading && (!allCartsData || allCartsData.summary.total_items === 0)) {
    return (
      <CartPageRoot className="min-h-full">
        <CartPageColumn className="py-16">
          <div className="mx-auto max-w-2xl">
            <NewEmptyCartState variant="no-carts" />
          </div>
        </CartPageColumn>
      </CartPageRoot>
    );
  }

  // Loading state
  if (isLoading) {
    return <CartPageSkeleton />;
  }

  // Data should be defined at this point due to early returns
  if (!allCartsData) {
    return <CartPageSkeleton />;
  }

  // The Filter earns its place only when the cart spans more than one supplier
  // or category - otherwise there is nothing to filter. Counts come from the
  // whole cart, not the filtered view, so applying a filter never hides it.
  const supplierCount = allCartsData.carts.length;
  const hasActiveFilter = !isAllSelected || !isAllCategoriesSelected;
  const showFilter = supplierCount >= 2 || categoryOptions.length >= 2;
  // Keep the toolbar (hence the Cart/Compare toggle) on the Compare view even
  // for a single-supplier cart, so a deep link to ?tab=review-matrix is never a
  // dead end with no way back to the cart.
  const showToolbar = showFilter || isMatrixView;

  // The Table view pins itself to the viewport from `md` (a phone's fixed bar
  // and short screen leave the grid too little room to be its own scroller).
  const fitsViewport = isMatrixView && !isPhone;

  // No matching filters (user has carts but all filtered out)
  if (filteredCarts.length === 0) {
    return (
      <CartPageRoot className="min-h-full">
        <CartPageHeader />

        <CartPageColumn>
          <NewEmptyCartState
            variant="no-matching-filters"
            onClearFilters={selectAll}
          />

          {/* None of the places that now hold Continue Shopping (summary card,
              Table view bar, phone content) render in this state, so without
              this the buyer's only way out is Clear Filters. */}
          <div className="flex justify-center pb-8 pt-5">
            <ContinueShoppingLink />
          </div>
        </CartPageColumn>
      </CartPageRoot>
    );
  }

  return (
    // `min-h-full` so the page's grey fills the scroll area on a short page;
    // otherwise the shell's slightly darker grey shows as a band underneath.
    <CartPageRoot
      className={
        // From `md` the Table view fits the viewport: the page stays put and
        // the grid is the one thing that scrolls. Two nested vertical scrolls
        // (page and grid) fight over the wheel.
        fitsViewport ? "flex h-full flex-col" : "min-h-full"
      }
    >
      <CartPageHeader />

      {/* The Table view is a wall of cells with the total bar pinned under it,
          so the floating buttons have no empty corner to sit in: lifted above
          the bar they land on the grid's last rows. The column (header too)
          stops short of the strip they occupy instead, from `md` and in every
          state, so they sit beside the grid and the bar, covering nothing and
          needing no lift. */}
      <CartPageColumn
        className={fitsViewport ? "flex min-h-0 flex-1 flex-col pb-3" : "pb-8"}
      >
        {/* Ad hoc supplier item paste-in (KAN-193) - Caroline's entry point
            for adding a hand-typed, fixed-price line she captured directly
            from a supplier's Shopify storefront. Never shown to the salon
            themselves - only a concierge operator session holding the
            capability token that authorizes the add. */}
        {isConciergeOperator && (
          <div className="mb-4 flex justify-end">
            <AdHocPasteInButton />
          </div>
        )}

        {/* What moved since the quote, when the cart was built for them by an
            operator. Fed the UNfiltered carts on purpose - the banner answers
            "what changed since this was built", a property of the cart, not of
            whatever the salon has currently filtered to. Sits above both views
            so a warning is never hidden behind the Compare toggle. Renders
            nothing when nothing changed. */}
        <QuoteDiffBanner carts={allCartsData?.carts ?? []} className="mb-4" />

        {/* Toolbar - replaces the permanent 256px filter rail (KAN-71). The
            filters live in its Filter popover, and it hosts the Cart / Compare
            toggle (KAN-72). Only shown when the cart actually spans multiple
            suppliers or categories - with nothing to filter or compare, there
            is nothing to offer. */}
        {showToolbar && (
          <CartToolbar
            // In Table view the toolbar is the grid's header: it butts against
            // the table (one shared edge, no gap) instead of floating above it
            // as a second card, which spent a band of height on air.
            className={
              isMatrixView ? "mb-0 rounded-b-none border-b-0" : undefined
            }
            showFilter={showFilter}
            hasActiveFilter={hasActiveFilter}
            filterContent={
              <CartFilterSidebar
                carts={allCartsData.carts}
                selectedCartIds={selectedCartIds}
                onToggleCart={toggleCart}
                onSelectAll={selectAll}
                isAllSelected={isAllSelected}
                totalSummary={allCartsData.summary}
                filteredSummary={filteredSummary}
                categories={categoryOptions}
                selectedCategoryIds={selectedCategoryIds}
                onToggleCategory={toggleCategory}
                onClearCategories={clearCategories}
                isAllCategoriesSelected={isAllCategoriesSelected}
              />
            }
            viewToggle={
              <CartViewToggle
                view={isMatrixView ? "compare" : "cart"}
                onViewChange={setView}
              />
            }
          >
            {/* Compare's key and hint ride in the toolbar rather than a strip of
                their own - one row of height back for the grid. */}
            {isMatrixView && (
              <>
                <div className="flex items-center gap-4 text-xs text-gray-500">
                  <span className="inline-flex items-center gap-1.5">
                    <span
                      className="h-3.5 w-3.5 rounded-[3px] ring-2 ring-inset ring-purple-500"
                      aria-hidden="true"
                    />
                    Your pick
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <span
                      className="h-3.5 w-3.5 rounded-full bg-green-500"
                      aria-hidden="true"
                    />
                    Cheapest
                  </span>
                </div>
                <p className="text-sm text-gray-600">
                  Tap a price to switch supplier
                </p>
              </>
            )}
          </CartToolbar>
        )}

        {isMatrixView ? (
          /* Compare - the one genuinely wide thing on the page, so it takes the
             full page width (main + summary together) rather than a column
             (KAN-72). The grid keeps its own horizontal scroll with the product
             column pinned. */
          <div
            className={cn(
              "w-full",
              // The grid sits flush under the toolbar (see CartToolbar above),
              // so it squares its top corners to meet it.
              "[&>div:first-child]:rounded-t-none",
              fitsViewport && "flex min-h-0 flex-1 flex-col"
            )}
          >
            <SupplierComparisonGrid
              carts={filteredCarts}
              onColumnCountChange={setMatrixColumnCount}
            />

            {/* Ad hoc rep-added items never appear as matrix rows (fixed price,
                no other supplier to compare against), so say so plainly rather
                than leaving the salon to wonder where they went. */}
            <CompareAdHocNote count={adHocLineCount} />

            {/* Above `md` the link lives in MatrixCheckoutBar, beside its
                button. The phone's fixed summary bar has no room for it, so
                it closes the page, and its bottom padding is the one 4rem that
                clears the fixed bar (the wrapper adds none, or it doubles). */}
            {isPhone && (
              <div className="mt-4 flex justify-center pb-16">
                <ContinueShoppingLink />
              </div>
            )}

            {/* The matrix has no summary rail, so it carries its own total and,
                unless an external-checkout supplier rules the combined
                checkout out, the same checkout action the Cart view shows. */}
            {isPhone ? (
              <MobileCartSummaryBar
                carts={filteredCarts}
                summary={filteredSummary}
                selectedCartIds={selectedCartIds}
                onCheckout={onCheckoutAll}
                isCheckoutDisabled={!hasSelection}
                saveDisabled={allCartsData.summary.total_items === 0}
              />
            ) : (
              <MatrixCheckoutBar
                summary={filteredSummary}
                onCheckout={onCheckoutAll}
                isCheckoutDisabled={!hasSelection}
              />
            )}
          </div>
        ) : isDesktop ? (
          /* Desktop (>= lg): two columns - seller cards, and the money roll-up
             pinned in the right rail. */
          <div className="flex flex-row gap-6">
            <main className="min-w-0 flex-1">
              <SellerCardList
                carts={filteredCarts}
                expandedCartIds={expandedCartIds}
                onToggleCard={toggleCard}
                appliedPromoCodes={appliedPromoCodes}
                promoErrors={promoErrors}
                onApplyPromo={applyPromo}
                onRemovePromo={removePromo}
              />
            </main>

            <aside className="w-[380px] flex-shrink-0">
              <div className="sticky top-[108px]">
                <OrderSummary
                  mode="cart"
                  carts={filteredCarts}
                  summary={filteredSummary}
                  selectedCartIds={selectedCartIds}
                  onCheckout={onCheckoutAll}
                  isCheckoutDisabled={!hasSelection}
                />

                {/* Snapshot the active carts into a reusable list (non-destructive).
                    The rail can reach the bottom of the window, where the
                    floating support button sits over its right-hand corner, so
                    the same right inset the fixed bars use keeps the button clear
                    of it. */}
                <div className={cn("mt-3", SUPPORT_FAB_BAR_CLEARANCE)}>
                  <SaveCartAsListButton
                    className="w-full"
                    disabled={allCartsData.summary.total_items === 0}
                  />
                </div>
              </div>
            </aside>
          </div>
        ) : (
          /* Below lg: one column. Cards run full width; the summary is a phone
             sticky bar with an expandable roll-up, or - on a tablet - an inline
             card beneath (KAN-73). Per-supplier checkout stays on each card. */
          <>
            <SellerCardList
              carts={filteredCarts}
              expandedCartIds={expandedCartIds}
              onToggleCard={toggleCard}
              appliedPromoCodes={appliedPromoCodes}
              promoErrors={promoErrors}
              onApplyPromo={applyPromo}
              onRemovePromo={removePromo}
            />

            {/* A phone has no inline summary card to hold the link, so it
                closes the list, with room beneath for the fixed summary bar
                (the 4rem the list itself used to reserve). When the bar has a
                checkout action, the line above it tells the two actions
                apart: a card's own button and the bar's both say "checkout",
                and only the bar's covers every selected supplier. With an
                external-checkout supplier the bar has no such action, so the
                hint is dropped. Tablet and desktop have the summary card in
                view with the total it applies to, so they need no hint. */}
            {isPhone && (
              <div className="flex flex-col items-center gap-4 pb-16 pt-5">
                {canCheckoutAll && (
                  <p className="max-w-xs text-center text-xs text-gray-500">
                    A card&apos;s Checkout orders from that supplier only.
                    Proceed to checkout orders from all selected suppliers.
                  </p>
                )}
                <ContinueShoppingLink />
              </div>
            )}

            {isPhone ? (
              <MobileCartSummaryBar
                carts={filteredCarts}
                summary={filteredSummary}
                selectedCartIds={selectedCartIds}
                onCheckout={onCheckoutAll}
                isCheckoutDisabled={!hasSelection}
                saveDisabled={allCartsData.summary.total_items === 0}
              />
            ) : (
              <div className="mt-6">
                <OrderSummary
                  mode="cart"
                  carts={filteredCarts}
                  summary={filteredSummary}
                  selectedCartIds={selectedCartIds}
                  onCheckout={onCheckoutAll}
                  isCheckoutDisabled={!hasSelection}
                />
                {/* Snapshot the active carts into a reusable list (non-destructive). */}
                <SaveCartAsListButton
                  className="mt-3 w-full"
                  disabled={allCartsData.summary.total_items === 0}
                />
              </div>
            )}
          </>
        )}
      </CartPageColumn>
    </CartPageRoot>
  );
}

/**
 * The sticky progress + title band shared by the loaded and filtered-empty
 * states, so the two cannot drift apart.
 */
function CartPageHeader() {
  return (
    <div className="sticky top-0 z-10 bg-gray-50 pb-3 pt-3">
      <CartPageColumn>
        {/* Checkout progress */}
        <CheckoutProgress currentStep="cart" />

        {/* Header */}
        {/* On a short viewport (landscape phone, small window) the breadcrumb
            already says "Cart", so this title row gives up its band to the
            content below 768px tall. */}
        <div className="mt-4 [@media(max-height:767px)]:hidden">
          <h1 className="text-2xl font-bold text-gray-900">Review your cart</h1>
        </div>
      </CartPageColumn>
    </div>
  );
}
